import { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import IngredientForm from './IngredientForm';
import USDAImporter from './USDAImporter';
import AIIngredientCreator from './AIIngredientCreator';
import AIKeysManager from './AIKeysManager';
import Button from '@mui/material/Button';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Settings } from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useAppContext } from '../context/AppContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import Popover from '@mui/material/Popover';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { formatNumber } from '../utils/formatNumber';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { MixIcon, InclusionIcon } from './IngredientIcons';

const IngredientList = ({ api }) => {
  const { ingredients, refreshIngredients, language, user } = useAppContext();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [myIngredientsOnly, setMyIngredientsOnly] = useState(false);
  const [inclusionOnly, setInclusionOnly] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editIngredient, setEditIngredient] = useState(null);
  const [cloneIngredient, setCloneIngredient] = useState(null);
  const [openUSDAImporter, setOpenUSDAImporter] = useState(false);
  const [openAICreator, setOpenAICreator] = useState(false);
  const [openAIKeysManager, setOpenAIKeysManager] = useState(false);
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuIngredient, setMenuIngredient] = useState(null);
  const [viewMoreOpen, setViewMoreOpen] = useState(false);
  const [viewMoreIngredient, setViewMoreIngredient] = useState(null);

  // Estado para columnas visibles
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('ingredientListVisibleColumns');
    return saved ? JSON.parse(saved) : {
      water: true,
      fats: true,
      sugars: true,
      other_solids: true,
      total_solids: true,
      pac: true,
      pod: true,
      milkfat: false,
      proteins: false,
      msnf: false,
      lactose: false,
      alcohol: false,
      salt: false,
      stabilizer: false,
      emulsifiers: false,
      hardening_factor: false
    };
  });
  const [columnSettingsAnchor, setColumnSettingsAnchor] = useState(null);

  // Categorías disponibles
  const categories = [
    'Sugar', 'Alcohol', 'Nut', 'Flavour', 'Stabilizer', 'Fruit',
    'Vegetable', 'Chocolate', 'Oil', 'Dairy', 'Egg', 
    'Emulsifier', 'Other'
  ];

  // Filtra ingredientes por nombre, traducciones, categoría, usuario e inclusión
  const filteredIngredients = ingredients.filter(ing => {
    // Filtro de búsqueda por texto
    const searchLower = search.toLowerCase();
    const nameMatch = ing.name && ing.name.toLowerCase().includes(searchLower);
    const categoryMatch = ing.category && ing.category.toLowerCase().includes(searchLower);
    const translationsMatch = ing.translations && Object.values(ing.translations).some(val =>
      val && val.toLowerCase().includes(searchLower)
    );
    const textMatch = nameMatch || categoryMatch || translationsMatch;
    
    // Filtro por categoría
    const categoryFilterMatch = !categoryFilter || ing.category === categoryFilter;
    
    // Filtro "Mis ingredientes" (solo si hay usuario logueado)
    const myIngredientsMatch = !myIngredientsOnly || (user && ing.user_id === user.id);
    
    // Filtro "Para inclusión (trozos/variegados)"
    const inclusionMatch = !inclusionOnly || ing.can_be_inclusion;
    
    return textMatch && categoryFilterMatch && myIngredientsMatch && inclusionMatch;
  });

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirm_delete_ingredient'))) return;
    try {
      await api.deleteIngredient(id);
      await refreshIngredients();
    } catch (error) {
      console.error('Error deleting ingredient:', error);
    } finally {
      setMenuAnchor(null);
      setMenuIngredient(null);
    }
  };

  const handleMenuOpen = (event, ing) => {
    setMenuAnchor(event.currentTarget);
    setMenuIngredient(ing);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuIngredient(null);
  };

  const handleUSDAImport = (ingredientData) => {
    setEditIngredient(ingredientData);
    setOpenForm(true);
    setOpenUSDAImporter(false);
  };

  const handleAIImport = (ingredientData) => {
    setEditIngredient(ingredientData);
    setOpenForm(true);
    setOpenAICreator(false);
  };

  const handleColumnToggle = (column) => {
    const newVisibleColumns = { ...visibleColumns, [column]: !visibleColumns[column] };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('ingredientListVisibleColumns', JSON.stringify(newVisibleColumns));
  };

  const availableColumns = [
    { key: 'water', label: t('water') },
    { key: 'fats', label: t('fats') },
    { key: 'sugars', label: t('sugars') },
    { key: 'proteins', label: t('proteins') },
    { key: 'other_solids', label: t('other_solids') },
    { key: 'total_solids', label: t('total_solids') },
    { key: 'milkfat', label: t('milkfat') },
    { key: 'msnf', label: t('msnf') },
    { key: 'lactose', label: t('lactose') },
    { key: 'pac', label: t('pac') },
    { key: 'pod', label: t('pod') },
    { key: 'alcohol', label: t('alcohol') },
    { key: 'salt', label: t('salt') },
    { key: 'stabilizer', label: t('stabilizer') },
    { key: 'emulsifiers', label: t('emulsifiers') },
    { key: 'hardening_factor', label: t('hardening_factor') }
  ];

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setEditIngredient(null);
              setCloneIngredient(null);
              setOpenForm(true);
            }}
          >
            {t('add_ingredient')}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setOpenUSDAImporter(true)}
          >
            {t('import_from_usda') || 'Import from USDA'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setOpenAICreator(true)}
          >
            {t('create_with_ai') || 'Create with AI'}
          </Button>
        </Box>
        
        {localStorage.getItem('token') && (
          <Button
            variant="text"
            onClick={() => setOpenAIKeysManager(true)}
            startIcon={<Settings />}
            title={t('aiIngredient.manageKeys')}
          >
            {t('aiKeys.title')}
          </Button>
        )}
      </Box>
      <IngredientForm
        open={openForm}
        api={api}
        ingredient={editIngredient || cloneIngredient}
        onClose={() => {
          setOpenForm(false);
          setEditIngredient(null);
          setCloneIngredient(null);
        }}
        onSubmit={() => {
          setOpenForm(false);
          setEditIngredient(null);
          setCloneIngredient(null);
          refreshIngredients();
        }}
      />
      
      {/* Filtros */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label={t('search')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, minWidth: '200px' }}
        />
        
        <FormControl size="small" sx={{ minWidth: '150px' }}>
          <InputLabel>{t('category')}</InputLabel>
          <Select
            value={categoryFilter}
            label={t('category')}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>{t('all_categories') || 'Todas'}</em>
            </MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat} value={cat}>
                {t(cat.toLowerCase())}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {user && (
          <FormControlLabel
            control={
              <Checkbox
                checked={myIngredientsOnly}
                onChange={(e) => setMyIngredientsOnly(e.target.checked)}
                size="small"
              />
            }
            label={t('my_ingredients_only') || 'Mis ingredientes'}
          />
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={inclusionOnly}
              onChange={(e) => setInclusionOnly(e.target.checked)}
              size="small"
            />
          }
          label={t('inclusion_only') || 'Para inclusión'}
        />

        {(categoryFilter || myIngredientsOnly || inclusionOnly) && (
          <Chip
            label={t('clear_filters') || 'Limpiar filtros'}
            onDelete={() => {
              setCategoryFilter('');
              setMyIngredientsOnly(false);
              setInclusionOnly(false);
            }}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('name')}</TableCell>
              {visibleColumns.water && <TableCell align="right">{t('water')}</TableCell>}
              {visibleColumns.fats && <TableCell align="right">{t('fats')}</TableCell>}
              {visibleColumns.sugars && <TableCell align="right">{t('sugars')}</TableCell>}
              {visibleColumns.proteins && <TableCell align="right">{t('proteins')}</TableCell>}
              {visibleColumns.other_solids && <TableCell align="right">{t('other_solids')}</TableCell>}
              {visibleColumns.total_solids && <TableCell align="right">{t('total_solids')}</TableCell>}
              {visibleColumns.milkfat && <TableCell align="right">{t('milkfat')}</TableCell>}
              {visibleColumns.msnf && <TableCell align="right">{t('msnf')}</TableCell>}
              {visibleColumns.lactose && <TableCell align="right">{t('lactose')}</TableCell>}
              {visibleColumns.pac && <TableCell align="right">{t('pac')}</TableCell>}
              {visibleColumns.pod && <TableCell align="right">{t('pod')}</TableCell>}
              {visibleColumns.alcohol && <TableCell align="right">{t('alcohol')}</TableCell>}
              {visibleColumns.salt && <TableCell align="right">{t('salt')}</TableCell>}
              {visibleColumns.stabilizer && <TableCell align="right">{t('stabilizer')}</TableCell>}
              {visibleColumns.emulsifiers && <TableCell align="right">{t('emulsifiers')}</TableCell>}
              {visibleColumns.hardening_factor && <TableCell align="right">{t('hardening_factor')}</TableCell>}
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={(e) => setColumnSettingsAnchor(e.currentTarget)}
                  title={t('column_settings')}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredIngredients.map(ing => (
              <TableRow key={ing.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{ing.translations[language] || ing.name}</span>
                    {ing.can_be_mix && (
                      <Tooltip title={t('can_be_mix') || 'Puede ir en mezcla'}>
                        <MixIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      </Tooltip>
                    )}
                    {ing.can_be_inclusion && (
                      <Tooltip title={t('can_be_inclusion') || 'Puede ser inclusión'}>
                        <InclusionIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                {visibleColumns.water && <TableCell align="right">{formatNumber(ing.water)}</TableCell>}
                {visibleColumns.fats && <TableCell align="right">{formatNumber(ing.fats)}</TableCell>}
                {visibleColumns.sugars && <TableCell align="right">{formatNumber(ing.sugars)}</TableCell>}
                {visibleColumns.proteins && <TableCell align="right">{formatNumber(ing.proteins)}</TableCell>}
                {visibleColumns.other_solids && <TableCell align="right">{formatNumber(ing.other_solids)}</TableCell>}
                {visibleColumns.total_solids && <TableCell align="right">{formatNumber(ing.total_solids)}</TableCell>}
                {visibleColumns.milkfat && <TableCell align="right">{formatNumber(ing.milkfat)}</TableCell>}
                {visibleColumns.msnf && <TableCell align="right">{formatNumber(ing.msnf)}</TableCell>}
                {visibleColumns.lactose && <TableCell align="right">{formatNumber(ing.lactose)}</TableCell>}
                {visibleColumns.pac && <TableCell align="right">{formatNumber(ing.pac)}</TableCell>}
                {visibleColumns.pod && <TableCell align="right">{formatNumber(ing.pod)}</TableCell>}
                {visibleColumns.alcohol && <TableCell align="right">{formatNumber(ing.alcohol)}</TableCell>}
                {visibleColumns.salt && <TableCell align="right">{formatNumber(ing.salt)}</TableCell>}
                {visibleColumns.stabilizer && <TableCell align="right">{formatNumber(ing.stabilizer)}</TableCell>}
                {visibleColumns.emulsifiers && <TableCell align="right">{formatNumber(ing.emulsifiers)}</TableCell>}
                {visibleColumns.hardening_factor && <TableCell align="right">{formatNumber(ing.hardening_factor)}</TableCell>}
                <TableCell align="center">
                  <Button
                    size="small"
                    onClick={e => handleMenuOpen(e, ing)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              setViewMoreIngredient(menuIngredient);
              setViewMoreOpen(true);
              handleMenuClose();
            }}
          >
            {t('view_more') || 'Ver más'}
          </MenuItem>
          {menuIngredient?.can_edit && (
            <MenuItem
              onClick={() => {
                setEditIngredient(menuIngredient);
                setCloneIngredient(null);
                setOpenForm(true);
                handleMenuClose();
              }}
            >
              {t('edit')}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              const { id, ...rest } = menuIngredient;
              setCloneIngredient({ ...rest });
              setEditIngredient(null);
              setOpenForm(true);
              handleMenuClose();
            }}
          >
            {t('clone')}
          </MenuItem>
          {menuIngredient?.can_delete && (
            <MenuItem
              onClick={() => {
                handleDelete(menuIngredient.id);
                handleMenuClose();
              }}
              sx={{ color: 'error.main' }}
            >
              {t('delete')}
            </MenuItem>
          )}
        </Menu>
      </TableContainer>

      {/* Popover para configuración de columnas */}
      <Popover
        open={Boolean(columnSettingsAnchor)}
        anchorEl={columnSettingsAnchor}
        onClose={() => setColumnSettingsAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 220, maxHeight: 400, overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('visible_columns') || 'Columnas visibles'}
          </Typography>
          <FormGroup>
            {availableColumns.map(col => (
              <FormControlLabel
                key={col.key}
                control={
                  <Checkbox
                    checked={visibleColumns[col.key]}
                    onChange={() => handleColumnToggle(col.key)}
                    size="small"
                  />
                }
                label={col.label}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>

      <Dialog open={viewMoreOpen} onClose={() => setViewMoreOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('ingredient_details')}</DialogTitle>
        <DialogContent>
          {viewMoreIngredient && (
            <Table size="small">
              <TableBody>
                {Object.entries(viewMoreIngredient).filter(([key]) => !['updated_at', 'created_at', 'translations', 'id', 'user_id', 'can_delete', 'can_edit'].includes(key)).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 600, width: 120 }}>
                      {key === 'name' ? t('name.en') : (t(key) || key)}
                    </TableCell>
                    <TableCell>
                      {typeof value === 'object' && value !== null
                        ? Array.isArray(value)
                          ? value.length > 0
                            ? JSON.stringify(value)
                            : ''
                          : JSON.stringify(value)
                        : ( key === "category" ? t(value.toLowerCase()) : String(value))}
                    </TableCell>
                  </TableRow>
                ))}
                {viewMoreIngredient && viewMoreIngredient.translations && (
                  Object.entries(viewMoreIngredient.translations).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ fontWeight: 600 }}>{t(`name.${key}`)}</TableCell>
                      <TableCell>
                        {value}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewMoreOpen(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
      
      <USDAImporter
        open={openUSDAImporter}
        onClose={() => setOpenUSDAImporter(false)}
        onImport={handleUSDAImport}
      />
      
      <AIIngredientCreator
        open={openAICreator}
        onClose={() => setOpenAICreator(false)}
        onImport={handleAIImport}
      />
      
      <AIKeysManager
        open={openAIKeysManager}
        onClose={() => setOpenAIKeysManager(false)}
        onKeysUpdated={() => {
          // Refresh or update any needed state
        }}
      />
    </>
  );
};

export default IngredientList;